import { validateSchemaValue, type StructuredSchema } from "./structured-schema";
import type { OocPresetDocument } from "./gameplay";
import { normalizeWorldCardSuggestions, type WorldCardSuggestion } from "./card-suggestions";
import { freeformSystemPacks, normalizeSystemPackSettings, type SystemPackSettings } from "./system-packs";
import type { ContextTriggerMode, ContextVisibility } from "./types";
import { normalizeStoryMap, type StoryMapDocument } from "./maps";
import { normalizeLayoutDocument, type LayoutDocument, type LayoutSource, type LayoutVersion } from "./layouts";
import type { FileViewMode, LayoutComponent } from "./templates";
import { DEFAULT_PROJECT_THEME, normalizeProjectTheme, normalizeStoryPlayMode, type ProjectTheme, type StoryPlayMode, type StoryPresentationVariant } from "./themes";

export type ProductDocumentKind = "bot" | "session" | "settings";

export interface StoryMessageDocument {
  id: number;
  role: "AI" | "PLAYER" | "MEDIA";
  body: string;
  enabled: boolean;
  variants: string[];
  activeVariantIndex: number;
  contextExcluded: boolean;
  generationPurpose: string;
}

export interface StoryBranchDocument extends Record<string, unknown> {
  id: string;
  name: string;
  messages: StoryMessageDocument[];
}

export interface StorySaveSlotDocument extends Record<string, unknown> {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  messages: StoryMessageDocument[];
  personaMemory: string;
  autoSummary: string;
  playerName: string;
  playerImageUris: string[];
  playerNameColorHex: string;
  profiles: ProfileCardDocument[];
  loreCards: LoreCardDocument[];
  branches: StoryBranchDocument[];
  responsesSinceSummary: number;
  totalCostThb: number;
}

export type CharacterCreationMode = "preset" | "quick_ai" | "guided";

export type CardTemplateFieldType = "array" | "object" | "image" | "text" | "long_text" | "number" | "level" | "select" | "multi_select" | "boolean" | "reference" | "stat_group";
export type StructuredCardStatus = "blank" | "draft" | "ready";
export type StructuredCardSource = "manual" | "assistant" | "import" | "legacy" | "preset";

export interface CardTemplateFieldOption extends Record<string, unknown> {
  id: string;
  label: string;
  value: string | number;
}

export interface CardTemplateFieldDocument extends Record<string, unknown> {
  id: string;
  key: string;
  label: string;
  description: string;
  type: CardTemplateFieldType;
  required: boolean;
  options: CardTemplateFieldOption[];
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: unknown;
  referenceCategoryIds: string[];
}

export interface CardCategoryDocument extends Record<string, unknown> {
  /** Menu preference only; does not change agent discovery or player permissions. */
  navigationVisible?: boolean;
  id: string;
  name: string;
  description: string;
  templateId: string;
  order: number;
  enabled: boolean;
  visibleToPlayers: boolean;
  displayStyle: "grid" | "list" | "feature_grid";
  icon: string;
  updatedAt: number;
}

export interface CardTemplateDocument extends Record<string, unknown> {
  agentEditable?: boolean;
  id: string;
  categoryId: string;
  name: string;
  description: string;
  version: number;
  fields: CardTemplateFieldDocument[];
  /** Legacy templates may omit layout; normalization always supplies a stable-id document. */
  layout?: LayoutDocument;
  layouts?: Partial<Record<FileViewMode, LayoutDocument>>;
  layoutVersions?: LayoutVersion[];
  assistantInstruction: string;
  contentType: "json" | "markdown";
  designation: string;
  contextVisibility: ContextVisibility;
  imageStyleInstruction: string;
  showCoverImages: boolean;
  enabled: boolean;
  updatedAt: number;
}

export interface StructuredCardDocument extends Record<string, unknown> {
  id: string;
  categoryId: string;
  templateId: string;
  templateVersion: number;
  title: string;
  values: Record<string, unknown>;
  status: StructuredCardStatus;
  source: StructuredCardSource;
  summary: string;
  images: string[];
  triggers: string[];
  contextVisibility?: ContextVisibility;
  triggerMode?: ContextTriggerMode;
  enabled: boolean;
  visibleToPlayers: boolean;
  updatedAt: number;
}

export interface CardGenerationRequest extends Record<string, unknown> {
  id: string;
  categoryId: string;
  templateId: string;
  count: number;
  brief: string;
  sourceInstructionIds: string[];
  blankOnMissing: boolean;
  mode: "single" | "bulk";
}

export interface ProfileCardDocument extends Record<string, unknown> {
  id: string;
  name: string;
  type: string;
  details: string;
  triggers: string;
  enabled: boolean;
  pinned: boolean;
  images: string[];
  displayColorHex: string;
  visibleToPlayers: boolean;
  playable: boolean;
  updatedAt: number;
}

export interface LoreCardDocument extends Record<string, unknown> {
  id: string;
  title: string;
  content: string;
  triggers: string;
  category: string;
  priority: number;
  enabled: boolean;
  visibleToPlayers: boolean;
  images: string[];
  pinned: boolean;
  updatedAt: number;
}

export interface CreatorInstructionDocument extends Record<string, unknown> {
  id: string;
  title: string;
  content: string;
  summary: string;
  triggers: string[];
  priority: number;
  pinned: boolean;
  enabled: boolean;
  updatedAt: number;
}

export interface CharacterCreationStepDocument extends Record<string, unknown> {
  id: string;
  kind: string;
  question: string;
  description: string;
  resultKey: string;
  optionCardIds: string[];
  locked: boolean;
}

export interface StartSceneDocument extends Record<string, unknown> {
  id: string;
  title: string;
  description: string;
  openingScene: string;
  image: string;
  characterIds: string[];
  enabled: boolean;
}

export type LandingSectionKind = "intro" | "start_scenes" | "characters" | "gallery" | "world_library" | "project_stats" | "custom";

export interface LandingBlockDocument extends Record<string, unknown> {
  id: string;
  image: string;
  title: string;
  body: string;
  fileId: string;
  mediaType?: "image" | "video" | "gif";
  poster?: string;
}

export interface LandingPresentation extends Record<string, unknown> {
  coverImage?: string;
  colorSource?: 'cover' | 'app';
  coverMotion?: string;
  coverMediaType?: "image" | "video" | "gif";
  titleImage?: string;
  backgroundColor?: string;
  blendCover?: boolean;
}

export interface LandingSectionDocument extends Record<string, unknown> {
  id: string;
  kind: LandingSectionKind;
  title: string;
  subtitle: string;
  enabled: boolean;
  itemIds: string[];
  layout?: "paired" | "horizontal";
  blocks?: LandingBlockDocument[];
}

/**
 * The durable account payload. Unknown keys are intentionally kept: an older
 * runtime must never erase a field introduced by a newer product surface.
 */
export interface AccountStoryDocument extends Record<string, unknown> {
  landingPresentation?: LandingPresentation;
  id: string;
  title: string;
  messages: StoryMessageDocument[];
  isBotTemplate: boolean;
  botDescription: string;
  botGender: string;
  botMode: string;
  playMode: StoryPlayMode;
  presentationVariant: StoryPresentationVariant;
  projectTheme: ProjectTheme;
  characterCreationEnabled: boolean;
  botVisibility: string;
  introCard: string;
  tags: string[];
  showcaseMedia: string[];
  backgroundImageUri: string;
  chatIconImageUri: string;
  homeHeroImageUri: string;
  backgroundBlur: number;
  chatFontSize: number;
  dialogueGradientPreset: string;
  dialogueColorIntensity: number;
  nameHighlightOpacity: number;
  characterCreationSteps: CharacterCreationStepDocument[];
  characterCreationModes: CharacterCreationMode[];
  characterCreationAiInstruction: string;
  profiles: ProfileCardDocument[];
  loreCards: LoreCardDocument[];
  creatorInstructions: CreatorInstructionDocument[];
  startScenes: StartSceneDocument[];
  landingSections: LandingSectionDocument[];
  cardCategories: CardCategoryDocument[];
  cardTemplates: CardTemplateDocument[];
  structuredCards: StructuredCardDocument[];
  storyMaps: StoryMapDocument[];
  activeMapId: string;
  sourceBotId: string;
  /** Immutable release selected when a play session starts. */
  releaseId: string;
  pendingReleaseId: string;
  releasePinnedAt: number;
  layoutSystemVersion: number;
  creatorOwned: boolean;
  botArchived: boolean;
  lastOpenedAt: number;
  createdAt: number;
  draftInput?: string;
  customOocPresets?: OocPresetDocument[];
  branches: StoryBranchDocument[];
  saveSlots: StorySaveSlotDocument[];
  systemPacks: SystemPackSettings;
  cardSuggestions: WorldCardSuggestion[];
}

const object = (value: unknown): Record<string, unknown> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const string = (value: unknown, fallback = "") => typeof value === "string" ? value : fallback;
const number = (value: unknown, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const boolean = (value: unknown, fallback = false) => typeof value === "boolean" ? value : fallback;
const strings = (value: unknown) => Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
const creationModes = (value: unknown): CharacterCreationMode[] => strings(value).filter((item): item is CharacterCreationMode => item === "preset" || item === "quick_ai" || item === "guided");
const fieldTypes: CardTemplateFieldType[] = ["array", "object", "image", "text", "long_text", "number", "level", "select", "multi_select", "boolean", "reference", "stat_group"];
const cardStatuses: StructuredCardStatus[] = ["blank", "draft", "ready"];
const cardSources: StructuredCardSource[] = ["manual", "assistant", "import", "legacy", "preset"];

export function normalizeStoryMessage(value: unknown, index = 0): StoryMessageDocument {
  const source = object(value);
  const role = source.role === "MEDIA" || source.role === "media"
    ? "MEDIA"
    : source.role === "AI" || source.role === "assistant" ? "AI" : "PLAYER";
  const body = string(source.body, string(source.content));
  const rawVariants = strings(source.variants);
  const variants = rawVariants.length ? rawVariants : body ? [body] : [];
  return {
    id: Math.trunc(number(source.id, index + 1)), role, body,
    enabled: boolean(source.enabled, true), variants,
    activeVariantIndex: Math.max(0, Math.min(Math.trunc(number(source.activeVariantIndex, Math.max(0, variants.length - 1))), Math.max(0, variants.length - 1))),
    contextExcluded: role === "MEDIA" || boolean(source.contextExcluded), generationPurpose: string(source.generationPurpose, role === "MEDIA" ? "media" : "story"),
  };
}

const normalizeProfile = (value: unknown): ProfileCardDocument => {
  const source = object(value);
  return {
    ...source,
    id: string(source.id), name: string(source.name), type: string(source.type, "ตัวละคร"), details: string(source.details),
    triggers: string(source.triggers), enabled: boolean(source.enabled, true), pinned: boolean(source.pinned),
    images: strings(source.images), displayColorHex: string(source.displayColorHex, "#45B8FF"),
    visibleToPlayers: boolean(source.visibleToPlayers, true), playable: boolean(source.playable), updatedAt: number(source.updatedAt),
  };
};

const normalizeLore = (value: unknown): LoreCardDocument => {
  const source = object(value);
  return {
    ...source,
    id: string(source.id), title: string(source.title), content: string(source.content), triggers: string(source.triggers),
    category: string(source.category, "กฎโลก"), priority: Math.trunc(number(source.priority, 50)), enabled: boolean(source.enabled, true),
    visibleToPlayers: boolean(source.visibleToPlayers, true), images: strings(source.images), pinned: boolean(source.pinned), updatedAt: number(source.updatedAt),
  };
};

const normalizeCreatorInstruction = (value: unknown, index: number): CreatorInstructionDocument => {
  const source = object(value);
  return {
    ...source, id: string(source.id, `instruction-${index + 1}`), title: string(source.title), content: string(source.content), summary: string(source.summary),
    triggers: strings(source.triggers), priority: Math.trunc(number(source.priority, 70)), pinned: boolean(source.pinned), enabled: boolean(source.enabled, true), updatedAt: number(source.updatedAt),
  };
};

const normalizeBranch = (value: unknown, index: number): StoryBranchDocument => {
  const source = object(value);
  return { ...source, id: string(source.id, `branch-${index + 1}`), name: string(source.name, `แขนง ${index + 1}`), messages: Array.isArray(source.messages) ? source.messages.map(normalizeStoryMessage) : [] };
};

const normalizeSaveSlot = (value: unknown, index: number): StorySaveSlotDocument => {
  const source = object(value);
  return {
    ...source, id: string(source.id, `save-${index + 1}`), name: string(source.name, `บันทึก ${index + 1}`),
    createdAt: number(source.createdAt), updatedAt: number(source.updatedAt),
    messages: Array.isArray(source.messages) ? source.messages.map(normalizeStoryMessage) : [],
    personaMemory: string(source.personaMemory), autoSummary: string(source.autoSummary), playerName: string(source.playerName),
    playerImageUris: strings(source.playerImageUris), playerNameColorHex: string(source.playerNameColorHex, "#45B8FF"),
    profiles: Array.isArray(source.profiles) ? source.profiles.map(normalizeProfile) : [], loreCards: Array.isArray(source.loreCards) ? source.loreCards.map(normalizeLore) : [],
    branches: Array.isArray(source.branches) ? source.branches.map(normalizeBranch) : [],
    responsesSinceSummary: Math.max(0, Math.trunc(number(source.responsesSinceSummary))), totalCostThb: number(source.totalCostThb),
  };
};

const normalizeCreationStep = (value: unknown, index: number): CharacterCreationStepDocument => {
  const source = object(value);
  return {
    ...source,
    id: string(source.id, `step-${index + 1}`), kind: string(source.kind, "text"), question: string(source.question),
    description: string(source.description), resultKey: string(source.resultKey), optionCardIds: strings(source.optionCardIds), locked: boolean(source.locked),
  };
};

const normalizeStartScene = (value: unknown, index: number): StartSceneDocument => {
  const source = object(value);
  return {
    ...source, id: string(source.id, `scene-${index + 1}`), title: string(source.title), description: string(source.description),
    openingScene: string(source.openingScene), image: string(source.image), characterIds: strings(source.characterIds), enabled: boolean(source.enabled, true),
  };
};

const landingKinds: LandingSectionKind[] = ["intro", "start_scenes", "characters", "gallery", "world_library", "project_stats", "custom"];
const normalizeLandingSection = (value: unknown, index: number): LandingSectionDocument => {
  const source = object(value);
  const kind = landingKinds.includes(source.kind as LandingSectionKind) ? source.kind as LandingSectionKind : "intro";
  return {
    ...source, id: string(source.id, `landing-${index + 1}`), kind, title: string(source.title), subtitle: string(source.subtitle),
    enabled: boolean(source.enabled, true), itemIds: strings(source.itemIds),
    layout: source.layout === "horizontal" ? "horizontal" : "paired",
    blocks: Array.isArray(source.blocks) ? source.blocks.map((value, blockIndex) => {
      const block = object(value);
      return { ...block, id: string(block.id, `block-${blockIndex + 1}`), image: string(block.image), title: string(block.title), body: string(block.body), fileId: string(block.fileId), mediaType: block.mediaType === "video" ? "video" : block.mediaType === "gif" ? "gif" : "image", poster: string(block.poster) };
    }) : [],
  };
};

const normalizeFieldOption = (value: unknown, index: number): CardTemplateFieldOption => {
  const source = object(value);
  const rawValue = typeof source.value === "number" || typeof source.value === "string" ? source.value : string(source.label);
  return { ...source, id: string(source.id, `option-${index + 1}`), label: string(source.label), value: rawValue };
};

const normalizeTemplateField = (value: unknown, index: number): CardTemplateFieldDocument => {
  const source = object(value);
  const type = fieldTypes.includes(source.type as CardTemplateFieldType) ? source.type as CardTemplateFieldType : "text";
  const result: CardTemplateFieldDocument = {
    ...source,
    id: string(source.id, `field-${index + 1}`), key: string(source.key, `field_${index + 1}`), label: string(source.label, `Field ${index + 1}`),
    description: string(source.description), type, required: boolean(source.required),
    options: Array.isArray(source.options) ? source.options.map(normalizeFieldOption) : [],
    referenceCategoryIds: strings(source.referenceCategoryIds),
  };
  if (source.min != null) result.min = number(source.min);
  if (source.max != null) result.max = number(source.max);
  if (source.step != null) result.step = number(source.step, 1);
  if (Object.hasOwn(source, "defaultValue")) result.defaultValue = source.defaultValue;
  return result;
};

const normalizeCardCategory = (value: unknown, index: number): CardCategoryDocument => {
  const source = object(value);
  const displayStyle = source.displayStyle === "list" || source.displayStyle === "feature_grid" ? source.displayStyle : "grid";
  return {
    ...source, id: string(source.id, `category-${index + 1}`), name: string(source.name, `หมวด ${index + 1}`), description: string(source.description),
    templateId: string(source.templateId), order: Math.trunc(number(source.order, index)), enabled: boolean(source.enabled, true),
    visibleToPlayers: boolean(source.visibleToPlayers, true), displayStyle, icon: string(source.icon, "◇"), updatedAt: number(source.updatedAt),
  };
};

function fallbackTemplateLayouts(fields: CardTemplateFieldDocument[]): Record<FileViewMode, LayoutDocument> {
  const component = (field: CardTemplateFieldDocument): LayoutComponent => ({
    id: `field-${field.key}`,
    type: field.type === "long_text" ? "markdown" : field.type === "number" || field.type === "level" ? "stat_grid" : field.type === "boolean" ? "toggle" : field.type === "reference" ? "reference_grid" : field.type === "multi_select" ? "badge_list" : field.type === "stat_group" ? "field_list" : "text",
    title: field.label,
    binding: `fields.${field.key}`,
  });
  return {
    full: normalizeLayoutDocument([{ id: "full-schema-fallback", type: "stack", children: fields.map(component) }]),
    compact: normalizeLayoutDocument([{ id: "compact-schema-fallback", type: "grid", columns: 2, children: fields.slice(0, 6).map(component) }]),
    mention: normalizeLayoutDocument([{ id: "mention-title", type: "text", binding: "title", styleToken: "accent" }]),
  };
}

function prependCoverToLayout(document: LayoutDocument): LayoutDocument {
  if (Object.values(document.elements).some((element) => element.type === "image_header")) return document;
  const next = structuredClone(document);
  const coverId = "file-cover" in next.elements ? "file-cover-migrated" : "file-cover";
  next.elements[coverId] = { type: "image_header", binding: "fields.image", children: [] };
  if (!next.root) next.root = coverId;
  else if (next.elements[next.root]?.type === "stack") next.elements[next.root]!.children.unshift(coverId);
  else {
    const previousRoot = next.root;
    let rootId = "layout-root-migrated";
    let suffix = 2;
    while (rootId in next.elements) rootId = `layout-root-migrated-${suffix++}`;
    next.elements[rootId] = { type: "stack", children: [coverId, previousRoot] };
    next.root = rootId;
  }
  return next;
}

const normalizeCardTemplate = (value: unknown, index: number): CardTemplateDocument => {
  const source = object(value);
  const contextVisibility = ["pinned", "partial", "searchable", "hidden"].includes(String(source.contextVisibility))
    ? source.contextVisibility as ContextVisibility : "searchable";
  const layoutsSource = object(source.layouts);
  const layouts: Partial<Record<FileViewMode, LayoutDocument>> = {};
  for (const mode of ["full", "compact", "mention"] as const) if (layoutsSource[mode]) layouts[mode] = normalizeLayoutDocument(layoutsSource[mode] as LayoutSource);
  const fields = Array.isArray(source.fields) ? source.fields.map(normalizeTemplateField) : [];
  let normalizedLayout = normalizeLayoutDocument(source.layout as LayoutSource | undefined);
  const fallbacks = fallbackTemplateLayouts(fields);
  const hasConfiguredLayout = Boolean(normalizedLayout.root || Object.keys(layouts).length);
  if (!hasConfiguredLayout) Object.assign(layouts, fallbacks);
  const layoutVersions = Array.isArray(source.layoutVersions) ? source.layoutVersions.map((entry, versionIndex) => {
    const version = object(entry);
    const viewMode: FileViewMode = version.viewMode === "compact" || version.viewMode === "mention" ? version.viewMode : "full";
    return {
      id: string(version.id, `layout-version-${versionIndex + 1}`), fileTypeId: string(version.fileTypeId, string(source.id)),
      version: Math.max(1, Math.trunc(number(version.version, versionIndex + 1))), viewMode,
      definition: normalizeLayoutDocument(version.definition as LayoutSource),
      source: ["system", "preset", "manual", "orbit", "import"].includes(String(version.source)) ? version.source as LayoutVersion["source"] : "import",
      createdAt: string(version.createdAt, new Date(0).toISOString()), createdBy: string(version.createdBy, "unknown"),
      ...(string(version.summary) ? { summary: string(version.summary) } : {}),
    } satisfies LayoutVersion;
  }) : [];
  const templateId = string(source.id, `template-${index + 1}`);
  const migrateUntouchedRecipeCover = /^(tmpl-freeform-|tmpl-rpg-)/.test(templateId)
    && Math.max(1, Math.trunc(number(source.version, 1))) === 1
    && layoutVersions.length === 0
    && boolean(source.showCoverImages, true);
  if (migrateUntouchedRecipeCover) {
    normalizedLayout = prependCoverToLayout(normalizedLayout);
    if (layouts.full) layouts.full = prependCoverToLayout(layouts.full);
    if (layouts.compact) layouts.compact = prependCoverToLayout(layouts.compact);
  }
  return {
    ...source, id: templateId, categoryId: string(source.categoryId), name: string(source.name, `Template ${index + 1}`),
    description: string(source.description), version: Math.max(1, Math.trunc(number(source.version, 1))),
    fields, assistantInstruction: string(source.assistantInstruction),
    layout: hasConfiguredLayout ? normalizedLayout : fallbacks.full, ...(Object.keys(layouts).length ? { layouts } : {}), ...(layoutVersions.length ? { layoutVersions } : {}),
    contentType: source.contentType === "markdown" ? "markdown" : "json", designation: string(source.designation), contextVisibility,
    imageStyleInstruction: string(source.imageStyleInstruction), showCoverImages: boolean(source.showCoverImages, true),
    enabled: boolean(source.enabled, true), updatedAt: number(source.updatedAt),
  };
};

const normalizeStructuredCard = (value: unknown, index: number): StructuredCardDocument => {
  const source = object(value);
  const status = cardStatuses.includes(source.status as StructuredCardStatus) ? source.status as StructuredCardStatus : "draft";
  const cardSource = cardSources.includes(source.source as StructuredCardSource) ? source.source as StructuredCardSource : "manual";
  const contextVisibility = ["pinned", "partial", "searchable", "hidden"].includes(String(source.contextVisibility))
    ? source.contextVisibility as ContextVisibility : undefined;
  const triggerMode = ["default", "narrative", "gm_tool"].includes(String(source.triggerMode))
    ? source.triggerMode as ContextTriggerMode : undefined;
  return {
    ...source, id: string(source.id, `card-${index + 1}`), categoryId: string(source.categoryId), templateId: string(source.templateId),
    templateVersion: Math.max(1, Math.trunc(number(source.templateVersion, 1))), title: string(source.title), values: object(source.values), status,
    source: cardSource, summary: string(source.summary), images: strings(source.images), triggers: strings(source.triggers), enabled: boolean(source.enabled, true),
    ...(contextVisibility ? { contextVisibility } : {}), ...(triggerMode ? { triggerMode } : {}), visibleToPlayers: boolean(source.visibleToPlayers, true), updatedAt: number(source.updatedAt),
  };
};

const defaultLandingSections: LandingSectionDocument[] = [
  { id: "project-stats", kind: "project_stats", title: "ข้อมูลเรื่อง", subtitle: "", enabled: true, itemIds: [] },
  { id: "intro", kind: "intro", title: "รู้จักโลกนี้", subtitle: "THE PREMISE", enabled: true, itemIds: [] },
  { id: "gallery", kind: "gallery", title: "GLIMPSES", subtitle: "", enabled: true, itemIds: [] },
  { id: "start-scenes", kind: "start_scenes", title: "เลือกจุดเริ่ม", subtitle: "การเดินทางของคุณ", enabled: true, itemIds: [] },
  { id: "characters", kind: "characters", title: "ตัวละครที่พบได้", subtitle: "ผู้คนในเรื่อง", enabled: true, itemIds: [] },
  { id: "world-library", kind: "world_library", title: "สำรวจโลกของเรื่อง", subtitle: "INSIDE THE WORLD", enabled: true, itemIds: [] },
];

export function normalizeAccountStoryDocument(value: unknown): AccountStoryDocument | null {
  const source = object(value);
  const id = string(source.id).trim();
  if (!id) return null;
  const messages = Array.isArray(source.messages) ? source.messages.map(normalizeStoryMessage) : [];
  const profiles = Array.isArray(source.profiles) ? source.profiles.map(normalizeProfile) : [];
  const loreCards = Array.isArray(source.loreCards) ? source.loreCards.map(normalizeLore) : [];
  const creatorInstructions = Array.isArray(source.creatorInstructions) ? source.creatorInstructions.map(normalizeCreatorInstruction) : [];
  const characterCreationSteps = Array.isArray(source.characterCreationSteps) ? source.characterCreationSteps.map(normalizeCreationStep) : [];
  const startScenes = Array.isArray(source.startScenes) ? source.startScenes.map(normalizeStartScene) : [];
  let landingSections = Array.isArray(source.landingSections) ? source.landingSections.map(normalizeLandingSection) : defaultLandingSections.map(normalizeLandingSection);
  // Only migrate the untouched six-section starter from before the layout editor.
  // A creator's order, custom blocks and titles take precedence.
  if (!source.landingLayoutVersion && landingSections.map(section => section.id).join(",") === "intro,start-scenes,characters,gallery,world-library,project-stats"
      && landingSections.every(section => !section.blocks?.length && !section.itemIds.length)
      && landingSections[4]?.subtitle === "เปิดอ่านเฉพาะส่วนที่สนใจ โดยไม่ยัดทั้งหมดเข้า AI") {
    landingSections = defaultLandingSections.map(fallback => ({ ...landingSections.find(section => section.id === fallback.id)!, subtitle: fallback.subtitle, title: fallback.title }));
  }
  const cardCategories = Array.isArray(source.cardCategories) ? source.cardCategories.map(normalizeCardCategory).sort((left, right) => left.order - right.order) : [];
  const cardTemplates = Array.isArray(source.cardTemplates) ? source.cardTemplates.map(normalizeCardTemplate) : [];
  const structuredCards = Array.isArray(source.structuredCards) ? source.structuredCards.map(normalizeStructuredCard) : [];
  const storyMaps = Array.isArray(source.storyMaps) ? source.storyMaps.map((item, index) => normalizeStoryMap(item, id, index)) : [];
  const branches = Array.isArray(source.branches) ? source.branches.map(normalizeBranch) : [];
  const saveSlots = Array.isArray(source.saveSlots) ? source.saveSlots.map(normalizeSaveSlot) : [];
  const normalizedModes = creationModes(source.characterCreationModes);
  const rpgFieldKeys = new Set(["hp", "hitpoints", "maxhitpoints", "armorclass", "initiative", "abilityscores", "spellslots", "damagedice"]);
  const hasRpgMechanics = cardTemplates.some((template) => template.fields.some((field) => rpgFieldKeys.has(field.key.toLocaleLowerCase())))
    || /\b(?:tabletop|d\s*&\s*d|dnd|armor class|hit points|dice roll)\b/i.test(string(source.systemInstruction));
  const playMode = normalizeStoryPlayMode(source.playMode, source.botMode, hasRpgMechanics);
  const presentationVariant: StoryPresentationVariant = source.presentationVariant === "dialogue" || source.botMode === "chat" ? "dialogue" : "standard";
  const normalizedSystemPacks = normalizeSystemPackSettings(source.systemPacks);
  if (!Object.hasOwn(source, "systemPacks") && playMode === "narrative_rpg") {
    normalizedSystemPacks.rpg = true;
    normalizedSystemPacks.dynamic_world = true;
  }
  return {
    ...source, id, title: string(source.title, "เรื่องใหม่"), messages, landingLayoutVersion: Math.max(2, number(source.landingLayoutVersion, 2)),
    landingPresentation: { ...Object.fromEntries(Object.entries(object(source.landingPresentation)).filter(([key])=>key!=="coverImage")),
      ...(typeof object(source.landingPresentation).coverImage === "string" ? { coverImage: string(object(source.landingPresentation).coverImage) } : {}),
      titleImage: string(object(source.landingPresentation).titleImage),
      backgroundColor: /^#[\da-f]{6}$/i.test(string(object(source.landingPresentation).backgroundColor)) ? string(object(source.landingPresentation).backgroundColor) : "",
      blendCover: boolean(object(source.landingPresentation).blendCover, true),
    },
    isBotTemplate: boolean(source.isBotTemplate), botDescription: string(source.botDescription),
    botGender: string(source.botGender, "Story"), botMode: string(source.botMode, "novel"), playMode, presentationVariant, projectTheme: normalizeProjectTheme(source.projectTheme),
    characterCreationEnabled: boolean(source.characterCreationEnabled), botVisibility: string(source.botVisibility, "private"),
    introCard: string(source.introCard), tags: strings(source.tags), showcaseMedia: strings(source.showcaseMedia),
    backgroundImageUri: string(source.backgroundImageUri), chatIconImageUri: string(source.chatIconImageUri), homeHeroImageUri: string(source.homeHeroImageUri),
    backgroundBlur: Math.max(0, Math.min(100, number(source.backgroundBlur, 80))), chatFontSize: Math.max(13, Math.min(24, number(source.chatFontSize, 16))),
    dialogueGradientPreset: string(source.dialogueGradientPreset, "sapphire"), dialogueColorIntensity: Math.max(.25, Math.min(1, number(source.dialogueColorIntensity, .88))),
    nameHighlightOpacity: Math.max(0, Math.min(1, number(source.nameHighlightOpacity, .72))),
    characterCreationSteps, characterCreationModes: normalizedModes.length ? normalizedModes : ["preset", "quick_ai", "guided"],
    characterCreationAiInstruction: string(source.characterCreationAiInstruction), profiles, loreCards, creatorInstructions, startScenes, landingSections,
    cardCategories, cardTemplates, structuredCards, storyMaps, activeMapId: string(source.activeMapId, storyMaps[0]?.id ?? ""), branches, saveSlots, systemPacks: normalizedSystemPacks,
    cardSuggestions: normalizeWorldCardSuggestions(source.cardSuggestions, id),
    sourceBotId: string(source.sourceBotId), releaseId: string(source.releaseId), pendingReleaseId: string(source.pendingReleaseId),
    releasePinnedAt: number(source.releasePinnedAt), layoutSystemVersion: Math.max(2, Math.trunc(number(source.layoutSystemVersion, 2))),
    creatorOwned: boolean(source.creatorOwned, true), botArchived: boolean(source.botArchived),
    lastOpenedAt: number(source.lastOpenedAt), createdAt: number(source.createdAt, Date.now()),
  };
}

export interface CreateBotInput {
  id: string;
  title: string;
  description?: string;
  openingScene?: string;
  systemInstruction?: string;
  gender?: string;
  mode?: string;
  playMode?: StoryPlayMode;
  presentationVariant?: StoryPresentationVariant;
  projectTheme?: ProjectTheme;
  visibility?: string;
  tags?: string[];
  characterCreationEnabled?: boolean;
  characterCreationSteps?: CharacterCreationStepDocument[];
  createdAt: number;
}

const defaultCharacterCreationSteps: CharacterCreationStepDocument[] = [
  { id: "player-name", kind: "text", question: "คุณชื่ออะไร", description: "ชื่อนี้จะแสดงในบทสนทนาและบันทึกของเส้นทาง", resultKey: "ชื่อ", optionCardIds: [], locked: true },
  { id: "player-age", kind: "text", question: "ระบุอายุของคุณ", description: "ข้อมูลพื้นฐานของ Persona ในเส้นทางนี้", resultKey: "อายุ", optionCardIds: [], locked: true },
];

export function createBotDocument(input: CreateBotInput): AccountStoryDocument {
  return normalizeAccountStoryDocument({
    id: input.id, title: input.title, messages: [], contextBudget: 40_000, maxInjectedEntries: 10,
    maxOutputTokens: 16_384, responseVerbosity: "medium", reasoningEffort: "medium", temperature: 1,
    contextPreset: "long_memory", provider: "Gemini", model: "", backendEnabled: true,
    backgroundImageUri: "", chatIconImageUri: "", homeHeroImageUri: "", openingScene: input.openingScene ?? "",
    openingDeliveryMode: "chat_seed", systemInstruction: input.systemInstruction ?? "", personaMemory: "", autoSummary: "",
    responsesSinceSummary: 0, autoSummaryEnabled: true, summaryModel: "deepinfra-deepseek-v4-flash",
    loreCards: [], profiles: [], creatorInstructions: [], branches: [], customOocPresets: [], saveSlots: [],
    lastOpenedAt: input.createdAt, createdAt: input.createdAt, isBotTemplate: true,
    botDescription: input.description ?? "", botGender: input.gender ?? "Story", botMode: input.mode ?? "novel",
    playMode: input.playMode ?? "freeform_novel", presentationVariant: input.presentationVariant ?? "standard", projectTheme: input.projectTheme ?? DEFAULT_PROJECT_THEME,
    characterCreationEnabled: input.characterCreationEnabled ?? false, botVisibility: input.visibility ?? "private",
    introCard: "", tags: input.tags ?? [], showcaseMedia: [], characterCreationSteps: input.characterCreationSteps ?? (input.characterCreationEnabled ? defaultCharacterCreationSteps : []),
    characterCreationModes: ["preset", "quick_ai", "guided"], characterCreationAiInstruction: "", startScenes: [], landingSections: defaultLandingSections,
    cardCategories: [], cardTemplates: [], structuredCards: [], storyMaps: [], activeMapId: "", systemPacks: freeformSystemPacks, cardSuggestions: [],
    sourceBotId: "", releaseId: "", pendingReleaseId: "", releasePinnedAt: 0, layoutSystemVersion: 2, creatorOwned: true, botArchived: false,
  })!;
}

export function createBlankStructuredCard(template: CardTemplateDocument, category: CardCategoryDocument, id: string, updatedAt = Date.now()): StructuredCardDocument {
  const values = Object.fromEntries(template.fields.map((field) => [field.key, Object.hasOwn(field, "defaultValue") ? field.defaultValue : null]));
  return {
    id, categoryId: category.id, templateId: template.id, templateVersion: template.version, title: "", values,
    status: "blank", source: "manual", summary: "", images: [], triggers: [], contextVisibility: template.contextVisibility,
    enabled: true, visibleToPlayers: category.visibleToPlayers, updatedAt,
  };
}

export interface StructuredCardValidationResult {
  values: Record<string, unknown>;
  errors: string[];
}

export function validateStructuredCardValues(template: CardTemplateDocument, input: Record<string, unknown>): StructuredCardValidationResult {
  const errors: string[] = [];
  const values: Record<string, unknown> = {};
  for (const field of template.fields) {
    const raw = Object.hasOwn(input, field.key) ? input[field.key] : Object.hasOwn(field, "defaultValue") ? field.defaultValue : null;
    let value: unknown = raw;
    if (field.readOnly === true || typeof field.expression === "string") continue;
    if (raw != null && raw !== "") {
      if (field.schema && typeof field.schema === "object") {
        validateSchemaValue(field.schema as StructuredSchema, raw, field.key, errors);
      } else if (field.type === "text" || field.type === "long_text") {
        if (typeof raw !== "string") { errors.push(`${field.key}: expected text`); value = null; }
      } else if (field.type === "number") {
        const numeric = typeof raw === "number" ? raw : Number(raw);
        if (!Number.isFinite(numeric) || (field.min != null && numeric < field.min) || (field.max != null && numeric > field.max)) {
          errors.push(`${field.key}: invalid number`); value = null;
        } else value = numeric;
      } else if (field.type === "boolean") {
        if (typeof raw !== "boolean") { errors.push(`${field.key}: expected boolean`); value = null; }
      } else if (field.type === "level" || field.type === "select") {
        const allowed = field.options.map((option) => option.value);
        if (!allowed.some((option) => option === raw)) { errors.push(`${field.key}: invalid option`); value = null; }
      } else if (field.type === "multi_select") {
        const allowed = field.options.map((option) => option.value);
        if (!Array.isArray(raw)) { errors.push(`${field.key}: expected option list`); value = null; }
        else {
          const selected = raw.filter((item) => allowed.some((option) => option === item));
          if (selected.length !== raw.length) errors.push(`${field.key}: contains invalid option`);
          value = selected;
        }
      } else if (field.type === "reference") {
        if (!(typeof raw === "string" || (Array.isArray(raw) && raw.every((item) => typeof item === "string")))) {
          errors.push(`${field.key}: expected reference id`); value = null;
        }
      } else if (field.type === "array") {
        if (!Array.isArray(raw)) { errors.push(`${field.key}: expected list`); value = null; }
      } else if (field.type === "image") {
        if (typeof raw !== "string" && (!raw || typeof raw !== "object" || Array.isArray(raw))) { errors.push(`${field.key}: expected image`); value = null; }
      } else if (field.type === "stat_group" || field.type === "object") {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) { errors.push(`${field.key}: expected stat object`); value = null; }
      }
    } else value = null;
    values[field.key] = value;
    if (field.required && (value == null || value === "" || (Array.isArray(value) && value.length === 0))) errors.push(`${field.key}: required`);
  }
  return { values, errors };
}

export function missingRequiredCardFields(template: CardTemplateDocument, card: StructuredCardDocument): string[] {
  return template.fields.filter((field) => {
    if (!field.required) return false;
    const value = card.values[field.key];
    return value == null || value === "" || (Array.isArray(value) && value.length === 0);
  }).map((field) => field.key);
}

export function instantiateSessionFromBot(bot: AccountStoryDocument, sessionId: string, createdAt: number): AccountStoryDocument {
  if (!bot.isBotTemplate || !sessionId.trim() || sessionId === bot.id) throw new Error("Play must clone a Studio master into a separate session");
  const opening = string(bot.openingScene);
  // An unpublished draft is a private snapshot, not a published release whose
  // identity can be inferred from schema versions (content can change alone).
  const releaseId = bot.releaseId || `${bot.id}:draft:${sessionId}`;
  return normalizeAccountStoryDocument({
    ...structuredClone(bot), id: sessionId, isBotTemplate: false, sourceBotId: bot.id, creatorOwned: false,
    botArchived: false, releaseId, pendingReleaseId: "", releasePinnedAt: createdAt, createdAt, lastOpenedAt: createdAt,
    messages: opening ? [{ id: 1, role: "AI", body: opening, enabled: true, variants: [opening], activeVariantIndex: 0, contextExcluded: false, generationPurpose: "opening_seed" }] : [],
  })!;
}

export function forkStudioBot(bot: AccountStoryDocument, forkId: string, createdAt: number): AccountStoryDocument {
  if (!bot.isBotTemplate || !forkId.trim() || forkId === bot.id) throw new Error("Studio fork requires a master and a new identity");
  return normalizeAccountStoryDocument({
    ...structuredClone(bot), id: forkId, isBotTemplate: true, creatorOwned: true, botVisibility: "private", botArchived: false,
    sourceBotId: "", releaseId: "", pendingReleaseId: "", releasePinnedAt: 0,
    clonedFromBotId: bot.id, messages: [], branches: [], saveSlots: [], draftInput: "", autoSummary: "", responsesSinceSummary: 0,
    createdAt, lastOpenedAt: createdAt,
  })!;
}
